import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { canonicalPathToFlattenPath } from '@procuros/datachecks';

import { SchemableFormNavigationContextProvider } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContextProvider';
import { useFlattenDataAndUIConfig } from 'hooks/useFlattenDataAndUIConfig';
import { SuccessfulEnrichment } from 'hooks/useGruAgent/types';
import { useIssueResolver } from 'hooks/useIssueResolver';
import { useCurrentUser } from 'services/repositories/user/user';
import { DisplayModes } from 'services/webedi/types';
import { WebEDIErrorsServiceSingleton } from 'services/webediErrors';
import { WebEDIAnnotation, WebEDIError } from 'services/webediErrors/types';
import { useNavigableAnnotations } from 'services/webediErrors/useNavigableAnnotations';
import {
  cleanAgentSuggestionStore,
  closeAgentSuggestionModal,
  useAgentSuggestionStore,
} from 'stores/agentSuggestion/agentSuggestion';
import {
  setFieldPathsWithAIEditions,
  setFieldPathsWithAISuggestions,
  setGruSuggestionEnrichment,
  setIsBigMessage,
  setUiConfig,
  useWebEDIStore,
} from 'stores/webedi/webedi';
import { isDefined } from 'support/helpers/generic/generic';
import { isBigMessage as checkIsBigMessage, isMessageBEValidationComplete } from 'support/helpers/messages/messages';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { isCurrentPartnerTheMessageReceiver } from 'support/helpers/relationships/relationships';
import {
  DocumentTypeWithVirtual,
  IssueResolverType,
  MessageV2DTO,
  PartnerDTO,
  ProcessSpecificationDTO,
  TransactionOriginApp,
  TransactionsRoutes,
} from 'support/types';
import { Form } from 'components/Form/Form';
import { LoadingOverlay } from 'components/Loading/LoadingOverlay';
import { Page, PAGE_SECTION_PORTAL_ROOT_ID } from 'components/Page/Page';
import { deriveDocumentDisplayConfig } from 'components/WebEDI/DocumentDisplayConfig/deriveDocumentDisplayConfig';
import { DocumentDisplayConfigProvider } from 'components/WebEDI/DocumentDisplayConfig/DocumentDisplayConfigContext';
import { DocumentSections } from 'components/WebEDI/DocumentSections/DocumentSections';
import { hasSummary } from 'components/WebEDI/DocumentSummary/helpers';
import { FieldAnnotationPopover } from 'components/WebEDI/FieldAnnotationPopover/FieldAnnotationPopover';
import { SidebarDocumentSummary } from 'components/WebEDI/SidebarDocumentSummary/SidebarDocumentSummary';
import {
  HiddenErrorsAdminBanner,
  ValidationPendingBanner,
} from 'components/WebEDI/WebEDIMessageErrorBanner/components';
import { WebEDIMessageErrorBanner } from 'components/WebEDI/WebEDIMessageErrorBanner/WebEDIMessageErrorBanner';
import { AgentSuggestionModal } from 'components/WebEDI/WebEDISidebar/AgentsTab/components/AgentSuggestionModal/AgentSuggestionModal';
import { useAgentsTabAvailability } from 'components/WebEDI/WebEDISidebar/hooks/useAgentsTabAvailability';
import { WebEDIAgentsTabAvailability, WebEDISidebarTabs } from 'components/WebEDI/WebEDISidebar/types';
import { WebEDISidebar } from 'components/WebEDI/WebEDISidebar/WebEDISidebar';

import { useGruAutomaticRetry } from '../hooks/useGruAutomaticRetry/useGruAutomaticRetry';
import { useGruEnrichedPaths } from '../hooks/useGruEnrichedPaths';
import { useGruSuggestionPaths } from '../hooks/useGruSuggestionPaths';
import { useMessageViewForm } from '../hooks/useMessageViewForm';
import { ClonedDocumentBanner } from '../../ClonedDocumentBanner/ClonedDocumentBanner';
import { isDocumentClone } from '../../ClonedDocumentBanner/helpers';
import { DroppedMessageAlert } from '../../DroppedMessageAlert/DroppedMessageAlert';
import { DuplicateAndChangeDocumentBanner } from '../../DuplicateAndChangeDocumentBanner/DuplicateAndChangeDocumentBanner';
import { shouldDetermineTheDuplicateAndChangeDocumentState } from '../../DuplicateAndChangeDocumentBanner/helpers';
import { ReviewBanner } from '../components/ReviewBanner/ReviewBanner';
import { shouldHideReviewBanner } from '../components/ReviewBanner/shouldHideReviewBanner';
import { MessageHeader } from '../MessageHeader/MessageHeader';

const DISPLAY_MODE = DisplayModes.view;
type MessageViewPageV2UIProps = {
  message: MessageV2DTO | undefined;
  messageOriginApp: TransactionOriginApp;
  canDownloadOriginalFile: boolean;
  relatedTransactions: Array<MessageV2DTO> | undefined;
  currentPartner: PartnerDTO | undefined;
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined;
};

export const ExistingMessageUI = ({
  message,
  messageOriginApp,
  canDownloadOriginalFile,
  relatedTransactions,
  currentPartner,
  processSpecFields,
}: MessageViewPageV2UIProps) => {
  const {
    annotations,
    blocking_error: blockingError,
    canonical,
    id: messageId,
    internal_state: internalState,
    issue_resolver_id: issueResolverId,
    receiver_partner: receiverPartner,
    review,
    sender_partner: senderPartner,
    type: documentType,
  } = message ?? {};
  const { data: flattenData, uiConfig } = useFlattenDataAndUIConfig(processSpecFields, canonical);

  useEffect(() => {
    if (uiConfig) {
      setUiConfig(uiConfig);
    }
  }, [uiConfig]);

  useEffect(() => {
    setIsBigMessage(checkIsBigMessage(flattenData ?? undefined));
  }, [flattenData]);

  const issueResolver = useIssueResolver(issueResolverId);
  const { formMethods, isLoading, isValidationComplete } = useMessageViewForm({
    flattenData,
    message,
    processSpecFields,
  });

  // Annotations are scoped to the issue resolver: only the partner who owns
  // the issue sees field highlights and the warning/error banner derived from
  // them. Other viewers (including Procuros admins impersonating a non-resolver
  // partner) get an empty singleton — admins inspecting annotations should
  // re-impersonate the actual issue resolver. Mirrors the existing
  // `shouldHideReviewBanner` CURRENT_PARTNER gate for the manual-review banner
  // displacement.
  const isIssueResolver = issueResolver === IssueResolverType.CURRENT_PARTNER;

  useNavigableAnnotations({
    formMethods,
    uiConfig,
    mode: DISPLAY_MODE,
    variables: receiverPartner ? { receiverPartnerName: receiverPartner.name } : {},
    documentType: documentType as DocumentTypeWithVirtual | undefined,
    senderPartner,
    receiverPartner,
    annotations: isIssueResolver ? annotations : undefined,
    canonical: canonical as Record<string, unknown> | undefined,
    flattenData: flattenData ?? undefined,
    enabled: Boolean(message && uiConfig),
  });

  const {
    agentsTabAvailability,
    isLoading: isAgentsTabAvailabilityLoading,
    showAgentsTab,
    hasIdentifierErrors,
    hasEnrichmentApplied,
    currentWorkflow,
    agentName,
  } = useAgentsTabAvailability({
    message,
    issueResolver,
    formErrors: formMethods.formState.errors,
    isValidationComplete,
  });

  // `undefined` while loading so the sidebar's `?? OVERVIEW` fallback applies only once the
  // decision is committed. Prevents passing a meaningful value before it's actually known.
  const initialDefaultTab = getInitialDefaultTab(isAgentsTabAvailabilityLoading, agentsTabAvailability);

  const { data: currentUser } = useCurrentUser();

  const { data: paths } = useGruEnrichedPaths({
    variables: {
      messageId,
      partnerId: currentPartner?.id,
      isAdmin: currentUser?.isAdmin,
      hasEnrichmentApplied,
    },
  });

  const { data: suggestionData } = useGruSuggestionPaths({
    variables: {
      messageId,
      partnerId: currentPartner?.id,
      isAdmin: currentUser?.isAdmin,
      showAgentsTab,
    },
  });

  useGruAutomaticRetry({ currentWorkflow, agentName });

  const fieldPathsWithAIEditions = useMemo(
    () =>
      new Set(
        Array.from(paths || [])
          .map((path) => canonicalPathToFlattenPath(path, canonical))
          .filter(isDefined),
      ),
    [paths, canonical],
  );

  const fieldPathsWithAISuggestions = useMemo(
    () =>
      new Set(
        Array.from(suggestionData?.paths || [])
          .map((path) => canonicalPathToFlattenPath(path, canonical))
          .filter(isDefined),
      ),
    [suggestionData?.paths, canonical],
  );

  useEffect(() => {
    setFieldPathsWithAIEditions(fieldPathsWithAIEditions);
  }, [fieldPathsWithAIEditions]);

  useEffect(() => {
    setFieldPathsWithAISuggestions(fieldPathsWithAISuggestions);
  }, [fieldPathsWithAISuggestions]);

  useEffect(() => {
    setGruSuggestionEnrichment(suggestionData?.enrichment ?? null);
  }, [suggestionData?.enrichment]);

  const navigate = useNavigate();
  const selectedSuggestion = useAgentSuggestionStore((state) => state.selectedSuggestion);
  const agentProposal = useAgentSuggestionStore((state) => state.agentProposal);

  const handleApplySuggestion = useCallback(() => {
    closeAgentSuggestionModal();
    if (message) {
      navigate(routeToPage(TransactionsRoutes.editSuggestion, { id: message.id }));
    }
  }, [message, navigate]);

  useEffect(() => {
    return () => {
      cleanAgentSuggestionStore();
    };
  }, []);

  const [isReprocessing, setIsReprocessing] = useState(false);

  const displayConfig = deriveDocumentDisplayConfig(message);
  const isBigMessage = useWebEDIStore((state) => state.isBigMessage);
  const hiddenErrors = useWebEDIStore((state) => state.hiddenErrors);
  const isLoadingDatachecksErrors = !isValidationComplete && Boolean(isBigMessage);
  const isMessageLoading = isBigMessage ? isLoading : isLoading || !isValidationComplete;
  const showValidationPendingBanner = isBigMessage && !isMessageBEValidationComplete(internalState) && !blockingError;
  const isReviewCompleted = isReviewComplete(review);
  const hideReviewBanner = shouldHideReviewBanner(message, issueResolver);

  return (
    <DocumentDisplayConfigProvider config={displayConfig}>
      <Page>
        <Form id="webedi-document">
          <FormProvider {...formMethods}>
            <SchemableFormNavigationContextProvider<WebEDIAnnotation>
              service={WebEDIErrorsServiceSingleton}
              NavigableItemPopoverComponent={FieldAnnotationPopover}
            >
              <Page.Head
                rightContent={
                  <ExistingMessageHeader
                    message={message}
                    messageOriginApp={messageOriginApp}
                    canDownloadOriginalFile={canDownloadOriginalFile}
                    currentPartner={currentPartner}
                    relatedTransactions={relatedTransactions}
                    onReprocessingChange={setIsReprocessing}
                  />
                }
              />
              <Page.Section
                isLoading={isMessageLoading}
                topBanner={
                  <ExistingMessageTopBanner
                    message={message}
                    currentPartnerId={currentPartner?.id}
                    hasFlattenData={Boolean(flattenData)}
                    issueResolver={issueResolver}
                    isLoadingDatachecksErrors={isLoadingDatachecksErrors}
                    showValidationPendingBanner={showValidationPendingBanner}
                    isReviewCompleted={isReviewCompleted}
                    hideReviewBanner={hideReviewBanner}
                  />
                }
                rightAside={
                  <WebEDISidebar
                    document={message}
                    documentOriginApp={messageOriginApp}
                    relatedTransactions={relatedTransactions}
                    currentPartner={currentPartner}
                    showAgentsTab={showAgentsTab}
                    hasIdentifierErrors={hasIdentifierErrors}
                    hasEnrichmentApplied={hasEnrichmentApplied}
                    isValidationComplete={isValidationComplete}
                    initialDefaultTab={initialDefaultTab}
                    isLoading={isAgentsTabAvailabilityLoading}
                  >
                    {hasSummary(documentType) ? <SidebarDocumentSummary documentType={documentType} /> : null}
                  </WebEDISidebar>
                }
              >
                <ExistingMessageDocumentContent
                  message={message}
                  currentUserIsAdmin={currentUser?.isAdmin}
                  hiddenErrors={hiddenErrors}
                  uiConfig={uiConfig}
                  isReviewCompleted={isReviewCompleted}
                  hideReviewBanner={hideReviewBanner}
                />
                <LoadingOverlay isVisible={isReprocessing} portalRootId={PAGE_SECTION_PORTAL_ROOT_ID} />
              </Page.Section>
            </SchemableFormNavigationContextProvider>
          </FormProvider>
        </Form>
      </Page>
      <ExistingMessageAgentSuggestionModal
        show={showAgentsTab}
        selectedSuggestion={selectedSuggestion}
        agentProposal={agentProposal}
        onApply={handleApplySuggestion}
        message={message}
        processSpecFields={processSpecFields}
        flattenData={flattenData}
        uiConfig={uiConfig}
      />
    </DocumentDisplayConfigProvider>
  );
};

type ExistingMessageHeaderProps = Pick<
  MessageViewPageV2UIProps,
  'message' | 'messageOriginApp' | 'canDownloadOriginalFile' | 'currentPartner' | 'relatedTransactions'
> & {
  onReprocessingChange: (isReprocessing: boolean) => void;
};

const ExistingMessageHeader = ({
  message,
  messageOriginApp,
  canDownloadOriginalFile,
  currentPartner,
  relatedTransactions,
  onReprocessingChange,
}: ExistingMessageHeaderProps) =>
  message ? (
    <MessageHeader
      mode={DISPLAY_MODE}
      message={message}
      messageOriginApp={messageOriginApp}
      canDownloadOriginalFile={canDownloadOriginalFile}
      currentPartner={currentPartner}
      relatedTransactions={relatedTransactions}
      onReprocessingChange={onReprocessingChange}
    />
  ) : null;

type ExistingMessageTopBannerProps = {
  message: MessageV2DTO | undefined;
  currentPartnerId: string | undefined;
  hasFlattenData: boolean;
  issueResolver: ReturnType<typeof useIssueResolver>;
  isLoadingDatachecksErrors: boolean;
  showValidationPendingBanner: boolean;
  isReviewCompleted: boolean;
  hideReviewBanner: boolean;
};

const ExistingMessageTopBanner = ({
  message,
  currentPartnerId,
  hasFlattenData,
  issueResolver,
  isLoadingDatachecksErrors,
  showValidationPendingBanner,
  isReviewCompleted,
  hideReviewBanner,
}: ExistingMessageTopBannerProps) => {
  const { annotations, blocking_error: blockingError, receiver_partner: receiverPartner } = message ?? {};

  return (
    <>
      {message && !isReviewCompleted && !hideReviewBanner && <ReviewBanner message={message} />}
      <DroppedMessageAlert message={message} />
      {shouldDetermineTheDuplicateAndChangeDocumentState(message) && (
        <DuplicateAndChangeDocumentBanner message={message} />
      )}
      {isDocumentClone(message) && <ClonedDocumentBanner message={message} />}
      {showValidationPendingBanner && <ValidationPendingBanner />}
      {(blockingError || (annotations && annotations.length > 0)) && hasFlattenData ? (
        <WebEDIMessageErrorBanner
          mode={DISPLAY_MODE}
          blockingError={blockingError ?? undefined}
          issueResolver={issueResolver}
          senderPartner={message?.sender_partner}
          receiverPartner={receiverPartner}
          isCurrentPartnerReceiver={isCurrentPartnerTheMessageReceiver(message, currentPartnerId)}
          isLoadingDatachecksErrors={isLoadingDatachecksErrors}
          messageState={message?.state}
        />
      ) : null}
    </>
  );
};

type ExistingMessageDocumentContentProps = {
  message: MessageV2DTO | undefined;
  currentUserIsAdmin: boolean | undefined;
  hiddenErrors: Array<WebEDIError>;
  uiConfig: ReturnType<typeof useFlattenDataAndUIConfig>['uiConfig'];
  isReviewCompleted: boolean;
  hideReviewBanner: boolean;
};

const ExistingMessageDocumentContent = ({
  message,
  currentUserIsAdmin,
  hiddenErrors,
  uiConfig,
  isReviewCompleted,
  hideReviewBanner,
}: ExistingMessageDocumentContentProps) =>
  message ? (
    <div className="flex flex-col gap-6">
      {isReviewCompleted && !hideReviewBanner && <ReviewBanner message={message} />}
      {currentUserIsAdmin && hiddenErrors.length > 0 ? <HiddenErrorsAdminBanner hiddenErrors={hiddenErrors} /> : null}
      <DocumentSections
        mode={DISPLAY_MODE}
        uiConfig={uiConfig}
        documentType={message.type}
        variables={message.receiver_partner ? { receiverPartnerName: message.receiver_partner.name } : {}}
      />
    </div>
  ) : null;

type ExistingMessageAgentSuggestionModalProps = Pick<MessageViewPageV2UIProps, 'message' | 'processSpecFields'> & {
  show: boolean;
  selectedSuggestion: string | null;
  agentProposal: SuccessfulEnrichment | null;
  onApply: () => void;
  flattenData: ReturnType<typeof useFlattenDataAndUIConfig>['data'];
  uiConfig: ReturnType<typeof useFlattenDataAndUIConfig>['uiConfig'];
};

const ExistingMessageAgentSuggestionModal = ({
  show,
  selectedSuggestion,
  agentProposal,
  onApply,
  message,
  processSpecFields,
  flattenData,
  uiConfig,
}: ExistingMessageAgentSuggestionModalProps) =>
  show ? (
    <AgentSuggestionModal
      isOpen={!!selectedSuggestion}
      onClose={closeAgentSuggestionModal}
      onApply={onApply}
      message={message}
      processSpecFields={processSpecFields}
      flattenData={flattenData}
      uiConfig={uiConfig}
      suggestion={selectedSuggestion ?? undefined}
      agentProposal={agentProposal ?? undefined}
    />
  ) : null;

const getInitialDefaultTab = (
  isLoading: boolean,
  availability: WebEDIAgentsTabAvailability,
): WebEDISidebarTabs | undefined =>
  isLoading
    ? undefined
    : availability === WebEDIAgentsTabAvailability.DEFAULT
      ? WebEDISidebarTabs.AGENTS
      : WebEDISidebarTabs.OVERVIEW;

const isReviewComplete = (review: MessageV2DTO['review']): boolean =>
  Boolean(review?.completed_by && review?.completed_at);
